/**
 * JSON Editor Control
 * JSON data editor with syntax validation
 * Performance: Memoized, useCallback for event handlers
 */

import React, { useCallback, memo } from 'react';
import { WarningIcon } from '@atlas-forms/ui-components-react';
import type { ControlRendererProps } from '../ControlRenderer';
import { getBaseControlClassName } from '../utils/classNameUtils';

const JsonEditorInput = memo(React.forwardRef<
  HTMLTextAreaElement,
  ControlRendererProps
>(
  (
    {
      control,
      value,
      onChange,
      onBlur,
      onFocus,
      error,
      touched,
      disabled,
      mode,
    },
    ref,
  ) => {
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const rawValue = e.target.value;
        try {
          // Try to parse as JSON and pass back as string
          // Actual JSON validation happens in form engine
          onChange(rawValue);
        } catch {
          // Keep raw string even if invalid JSON
          onChange(rawValue);
        }
      },
      [onChange],
    );

    const showError = touched && error;
    const containerClassName = getBaseControlClassName(control, {
      error: showError,
      disabled: disabled || control.disabled,
    });

    // Get rows from config or default
    const rows = (control.config?.rows as number) || 8;

    return (
      <div className={containerClassName}>
        {control.label && (
          <label htmlFor={control.id} className="control__label">
            <span className="control__label-text">{control.label}</span>
            {control.required && (
              <span className="control__required" aria-label="required">
                *
              </span>
            )}
          </label>
        )}

        <div className="control__input-wrapper">
          <textarea
            ref={ref}
            id={control.id}
            name={control.id}
            value={
              typeof value === 'string'
                ? value
                : JSON.stringify(value || {}, null, 2)
            }
            onChange={handleChange}
            onBlur={onBlur}
            onFocus={onFocus}
            placeholder={control.placeholder || '{\n  "key": "value"\n}'}
            disabled={disabled || control.disabled}
            readOnly={mode === 'view' || control.readonly}
            rows={rows}
            className="control__input control__input--json-editor"
            aria-invalid={showError ? 'true' : 'false'}
            aria-required={control.required ? 'true' : 'false'}
            aria-describedby={`${control.id}-error ${control.id}-help`}
            spellCheck={false}
          />
          {showError && (
            <span className="control__error-icon" aria-hidden="true">
              <WarningIcon width={14} height={14} />
            </span>
          )}
        </div>

        {control.helpText && (
          <div id={`${control.id}-help`} className="control__help">
            {control.helpText}
          </div>
        )}

        {showError && (
          <div
            id={`${control.id}-error`}
            className="control__error-message"
            role="alert"
          >
            {error}
          </div>
        )}
      </div>
    );
  },
));

JsonEditorInput.displayName = 'JsonEditorInput';

export default JsonEditorInput;
